import { z } from 'zod';

export type AccountFeatureValue =
  | boolean
  | number
  | string
  | string[]
  | { [key: string]: AccountFeatureValue };

const accountFeatureValueSchema: z.ZodType<AccountFeatureValue> = z.lazy(() =>
  z.union([
    z.boolean(),
    z.number(),
    z.string(),
    z.array(z.string()),
    z.record(z.string(), accountFeatureValueSchema),
  ]),
);

export const integrationSchema = z.object({
  integrationId: z.string(),
  service: z.preprocess(
    (v) => String(v).toLowerCase(),
    z.enum([
      'confluence',
      'custom_unstructured',
      'facebook_ads',
      'facebook_page',
      'google_ads',
      'google_analytics',
      'google_bigquery',
      'github',
      'google_drive',
      'mcp',
      'mixpanel',
      'notion',
      'shopify',
      'slack',
    ]),
  ),
  createdAt: z.string(),
  name: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  oauthClientId: z.string().nullable().optional(),
  authStatus: z.enum(['NOT_CONNECTED', 'CONNECTED', 'EXPIRED']).nullable().optional(),
});

export type IntegrationAuthStatus = 'NOT_CONNECTED' | 'CONNECTED' | 'EXPIRED';

export interface Integration {
  integrationId: string;
  service:
    | 'confluence'
    | 'custom_unstructured'
    | 'facebook_ads'
    | 'facebook_page'
    | 'google_ads'
    | 'google_analytics'
    | 'google_bigquery'
    | 'github'
    | 'google_drive'
    | 'mcp'
    | 'mixpanel'
    | 'notion'
    | 'shopify'
    | 'slack';
  createdAt: string;
  name?: string | null;
  description?: string | null;
  workspaceId?: string | null;
  oauthClientId?: string | null;
  authStatus?: IntegrationAuthStatus | null;
}

const connectionSchema = z.object({
  type: z.string(),
  connectionId: z.string(),
  name: z.string(),
});
export type Connection = z.infer<typeof connectionSchema>;

export const accountSchema: z.ZodType<Account> = z.object({
  accountId: z.string(),
  slug: z.string(),
  name: z.string(),
  timezone: z.string().nullable().optional(),
  isOnboarded: z.boolean(),
  features: z.record(z.string(), accountFeatureValueSchema).optional(),
  integrations: z.array(integrationSchema),
  connections: z.array(connectionSchema).optional(),
  pinnedExplorationIds: z.array(z.string()).optional(),
  me: z.object({
    pinnedExplorationIds: z.array(z.string()).optional(),
    permissions: z
      .array(z.object({ permissionId: z.string() }))
      .transform((permissions) => permissions.map((p) => p.permissionId)),
  }),
});

export interface Account {
  accountId: string;
  slug: string;
  name: string;
  timezone?: string | null;
  isOnboarded: boolean;
  features?: Record<string, AccountFeatureValue>;
  integrations: Integration[];
  connections?: Connection[];
  pinnedExplorationIds?: string[];
  me: { pinnedExplorationIds?: string[]; permissions: string[] };
}
